import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import { API_ENDPOINT } from "../../config";
import {
  GetInvoiceDto,
  Invoice,
  ListResponse,
  Payment,
  PaymentSuccessDto,
  RefundInvoiceDto,
  UpdateInvoiceDto,
} from "@/interfaces";
import { toastMessage } from "@/utils/toastError";

export const invoiceApi = createApi({
  reducerPath: "invoiceApi",
  baseQuery: fetchBaseQuery({
    baseUrl: API_ENDPOINT + "/admin/invoice",
    credentials: "include",
  }),
  endpoints: (build) => ({
    getInvoice: build.query<Invoice, string>({
      query: (id) => ({
        url: "/" + id,
        method: "GET",
      }),
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
    }),
    updateInvoice: build.mutation<Invoice, UpdateInvoiceDto>({
      query: (body) => ({
        url: "/",
        method: "PUT",
        body,
      }),
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
      transformResponse(baseQueryReturnValue: any) {
        toastMessage("success", "Successfully updated");
        return baseQueryReturnValue;
      },
    }),
    getInvoices: build.query<ListResponse<Invoice>, GetInvoiceDto>({
      query: (params) => ({
        url: "/",
        method: "GET",
        params,
      }),
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
    }),
    deleteInvoice: build.mutation<{ success: boolean; message: string }, { invoice_id: string }>({
      query: (body) => ({
        url: "/",
        method: "DELETE",
        body,
      }),
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
      transformResponse(baseQueryReturnValue: any) {
        toastMessage("success", baseQueryReturnValue);
        return baseQueryReturnValue;
      },
    }),
    // POST /admin/invoice/generate
    generateInvoices: build.mutation<{ success: boolean; message: string }, { year: string; month: string }>({
      query: (body) => ({
        url: "/generate",
        method: "POST",
        body,
      }),
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
      transformResponse(baseQueryReturnValue: any) {
        toastMessage("success", baseQueryReturnValue || "Invoices generated successfully");
        return baseQueryReturnValue;
      },
    }),
    // POST /admin/invoice/refund
    refundInvoice: build.mutation<{ success: boolean; message: string }, RefundInvoiceDto>({
      query: (body) => ({
        url: "/refund",
        method: "POST",
        body,
      }),
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
      transformResponse(baseQueryReturnValue: any) {
        toastMessage("success", baseQueryReturnValue);
        return baseQueryReturnValue;
      },
    }),
  }),
});

export const {
  useGetInvoicesQuery,
  useUpdateInvoiceMutation,
  useDeleteInvoiceMutation,
  useGetInvoiceQuery,
  useLazyGetInvoiceQuery,
  useGenerateInvoicesMutation,
  useRefundInvoiceMutation,
} = invoiceApi;
